import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import loadable from '@loadable/component';
import getGamePalette from '@helpers/getGamePalette';
import ModalBody from '@components/Modals/components/ModalBody';
import ModalBreadcrumb from '@components/Modals/components/ModalBreadcrumb';
import ModalHeader from '@components/Modals/components/ModalHeader';
import useAddGameBreadcrumbManager from '@hooks/useAddGameBreadcrumbManager';
import useRemoveAddGameParams from '@hooks/useRemoveAddGameParams';
import { setConnectRegionAction, setConnectPlatformAction, setConnectGameServiceAction, } from '../context/AddGameStepsActions';
import ContentCarousel from '../../UI/ContentCarousel';
import ContentCODLoginServices from './ContentCODLoginServices';
import ContentCODConnect from './ContentCODConnect';
import { useAddGameContext } from '../context/AddGameProvider';
import AddGameConnectStatus from '../AddGameConnectStatus';
import AddGameInterstitial from '../AddGameInterstitial';
import AddGameOAuthConnectContent from '../AddGameOAuthConnectContent';
import AgeRestriction from './AgeRestriction/AgeRestriction';
import getAnalyticsFlowStep from '@domain/Analytics/helpers/getAnalyticsFlowStep';
const GameIcon = loadable(() => import('@components/Icons/GameIcon'));
/**
 * A ModalBody for adding COD-Warzone
 * @param onStepChange - invoked whenever the steps changes. Returns the step index and current step name if available.
 * @param onConnectConfirm - invoked when user clicks "Confirm" in the connect success screen.
 * @param shouldGoToConnectionStatus - boolean to determine whether this view should be displaying connection status view.
 */
const AddCODWarzone2 = ({ onStepChange, onConnectConfirm, shouldShowSelectBreadcrumb = true, onBackToSelect, shouldGoToConnectionStatus = false, successButtonLabel = 'Continue', failButtonLabel = 'Retry', onClose, }) => {
    const gameKey = 'cod-warzone-2';
    // Breadcrumbs have more items than the steps. i.e. Select > Profile. This offset is used for breadcrumb/step relationship when navigating between steps
    const breadcrumbStepOffset = shouldShowSelectBreadcrumb ? 1 : 0;
    // Used to determine whether the Modal is ready to render. Currently only used for age check
    const [currentStepIndex, setCurrentStepIndex] = useState(shouldGoToConnectionStatus ? -1 : 0);
    const { dispatch, gameConnectionStatus, isProviderReady, fetched, connectGameService, selectedGame, isOverConsentAge, } = useAddGameContext();
    const gameStepBreadcrumbs = [
        {
            id: 'age-restriction',
            component: 'Age Restriction',
            analyticsFlowStep: isOverConsentAge ? 'age restriction confirmation' : 'age restricted',
        },
        {
            id: 'game-service',
            component: 'Service',
            analyticsFlowStep: 'select game platform',
        },
        {
            id: 'oauth-connect',
            component: 'Connect',
            analyticsFlowStep: 'connect game',
        },
        {
            id: 'username-connect',
            component: 'Connect',
            analyticsFlowStep: 'enter game information',
        },
    ];
    const removeAddGameParams = useRemoveAddGameParams();
    // NOTE: Age check is ALWAYS required, so it is not part of the ContentCarousel, thus we don't want the below hook to manage that requirement
    const { breadcrumbIndex, breadcrumbs, breadcrumbIsVisible, setBreadcrumbIsVisible, displayBreadcrumbsUpTo, getIndexOfBreadcrumb, } = useAddGameBreadcrumbManager({
        gameStepBreadcrumbs,
        startingIndex: breadcrumbStepOffset,
        includeSelectBreadcrumb: shouldShowSelectBreadcrumb,
        requireAgePrompt: false,
    });
    const getStepIndexFromBreadcrumbId = (relatedBreadcrumbName) => getIndexOfBreadcrumb(relatedBreadcrumbName) - breadcrumbStepOffset;
    const onRegionSelected = (region, service) => {
        dispatch(setConnectRegionAction(region));
        // Based on the selected game service, go to a different connect step
        const targetStepName = !!service.oauth ? 'oauth-connect' : 'username-connect';
        const newStepIndex = getStepIndexFromBreadcrumbId(targetStepName);
        setCurrentStepIndex(newStepIndex);
    };
    const updateDefaultPlatformAndRegionFromService = (service) => {
        // COD usually only has one platform
        if ((service === null || service === void 0 ? void 0 : service.platforms.length) === 1) {
            const selectedPlatform = service.platforms[0];
            dispatch(setConnectPlatformAction(selectedPlatform));
            // If there is only a single region, select it automatically
            const enabledRegions = selectedPlatform.regions.filter((r) => r.enabled);
            if (enabledRegions.length === 1) {
                onRegionSelected(enabledRegions[0], service);
            }
        }
    };
    const onLoginServiceSelected = (service) => {
        dispatch(setConnectGameServiceAction(service));
        setCurrentStepIndex(currentStepIndex + 1);
        updateDefaultPlatformAndRegionFromService(service);
    };
    const handleBreadcrumbClick = (idx, breadcrumb) => {
        if (breadcrumb.id === 'select') {
            // Invoke onBackToSelect to dismiss current view
            if (onBackToSelect) {
                onBackToSelect(gameKey);
            }
        }
        else if (idx >= breadcrumbStepOffset) {
            // Breadcrumb contains "Select" as first step thus pushing all index forward by 1
            setCurrentStepIndex(idx - breadcrumbStepOffset);
        }
    };
    const handleStepChange = (changedStepIndex, stepName) => {
        if (stepName === 'game-connect-status') {
            setBreadcrumbIsVisible(false);
        }
        else if (stepName === 'username-connect') {
            setBreadcrumbIsVisible(true);
            displayBreadcrumbsUpTo('username-connect', ['oauth-connect']);
        }
        else {
            setBreadcrumbIsVisible(true);
            displayBreadcrumbsUpTo(stepName);
        }
        if (onStepChange) {
            const analyticsFlowStep = getAnalyticsFlowStep(gameConnectionStatus, changedStepIndex, gameStepBreadcrumbs);
            onStepChange(changedStepIndex, stepName, analyticsFlowStep, gameStepBreadcrumbs.length);
        }
    };
    const handleContinue = () => {
        setCurrentStepIndex(currentStepIndex + 1);
    };
    const handleConnectSuccessClick = () => {
        if (onConnectConfirm) {
            onConnectConfirm();
        }
    };
    const handleConnectFailClick = () => {
        removeAddGameParams();
        if (connectGameService === null || connectGameService === void 0 ? void 0 : connectGameService.oauth) {
            setCurrentStepIndex(getStepIndexFromBreadcrumbId('oauth-connect'));
        }
        else {
            setCurrentStepIndex(getStepIndexFromBreadcrumbId('username-connect'));
        }
    };
    const handleClose = () => {
        const isSuccessPage = breadcrumbIndex === -1;
        if (onClose) {
            onClose(gameConnectionStatus === 'success', isSuccessPage);
        }
    };
    const onUsernameSubmit = () => {
        setCurrentStepIndex(currentStepIndex + 1);
    };
    return (_jsxs(_Fragment, { children: [_jsx(ModalHeader, { title: _jsx("span", { "data-ignore": true, children: selectedGame === null || selectedGame === void 0 ? void 0 : selectedGame.name }), icon: _jsx(GameIcon, { game: gameKey, showGameColor: true }), subtitle: "Connect your account", paletteName: getGamePalette(gameKey), onClose: handleClose }), _jsx(ModalBreadcrumb, { currentIndex: breadcrumbIndex, breadcrumbs: breadcrumbs, onClick: handleBreadcrumbClick, isVisible: breadcrumbIsVisible }), _jsxs(ModalBody, { children: [isProviderReady && (_jsxs(ContentCarousel, { name: "cod-warzone", currentStep: currentStepIndex, onStepChange: handleStepChange, children: [_jsx(AgeRestriction, { selectedGame: selectedGame, isOverConsentAge: isOverConsentAge, stepName: "age-restriction", onClick: handleContinue, onClose: handleClose }), _jsx(ContentCODLoginServices, { stepName: "game-service", onLoginServiceSelected: onLoginServiceSelected }), _jsx(AddGameOAuthConnectContent, { stepName: "oauth-connect" }), _jsx(ContentCODConnect, { stepName: "username-connect", onSubmit: onUsernameSubmit }), _jsx(AddGameConnectStatus, { onConfirm: handleConnectSuccessClick, onRetry: handleConnectFailClick, status: gameConnectionStatus || 'error', gameKey: gameKey, successButtonLabel: successButtonLabel, failButtonLabel: failButtonLabel, errorMessage: (fetched === null || fetched === void 0 ? void 0 : fetched.isError) ? fetched.errorMessage : undefined, isLoading: !connectGameService, stepName: "game-connect-status" })] })), !isProviderReady && _jsx(AddGameInterstitial, {})] })] }));
};
export default AddCODWarzone2;
